import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Camada global de tooltip — padroniza TODOS os tooltips do app.
 *
 * Em vez de trocar cada `title="..."` por um componente, esta camada "sequestra"
 * o atributo `title` nativo (que usa o tooltip do SO, com delay não-configurável)
 * e mostra o nosso tooltip escuro no lugar. Vale pra todo `title` atual E
 * futuro, sem tocar nos call-sites.
 *
 * Como funciona: no 1º hover de um elemento com `title`, move `title` →
 * `data-cp-tip` (suprime o nativo) e, se não houver `aria-label`, copia pra lá
 * (preserva acessibilidade). O React re-adiciona o `title` em re-render — o
 * próximo hover move de novo, mantendo `data-cp-tip` sempre atual. O tooltip é
 * `position: fixed` (não recorta em containers com overflow).
 *
 * Aparição: espera ATRASO_MS com o mouse em cima — sempre, inclusive ao passar
 * de um ícone pro vizinho — e entra com fade + deslize curto (DURACAO_MS /
 * DESLOCAMENTO_PX).
 */

/** Quanto tempo o mouse fica em cima antes do tooltip aparecer. */
const ATRASO_MS = 200;
/** Duração da animação de entrada. */
const DURACAO_MS = 150;
/** Quanto o tooltip desliza na entrada (vem do lado do elemento). */
const DESLOCAMENTO_PX = 4;

interface TipState {
  text: string;
  x: number;
  y: number;
  placement: 'top' | 'bottom';
}

const baseTransform = (placement: TipState['placement']): string =>
  `translateX(-50%)${placement === 'top' ? ' translateY(-100%)' : ''}`;

export function TooltipLayer() {
  const [tip, setTip] = useState<TipState | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Elemento com tooltip sob o mouse — esperando o atraso ou já visível.
    let alvo: HTMLElement | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // Elemento clicado: o tooltip dele fica calado até o mouse sair dele.
    // Sem isso, o clique escondia e o mouseover seguinte (passar do botão pro
    // ícone dentro dele) mostrava de novo.
    let silencedEl: HTMLElement | null = null;

    const hide = (): void => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      alvo = null;
      setTip(null);
    };

    // Move `title` → `data-cp-tip` na hora do hover (antes do atraso), senão
    // o tooltip nativo do SO teria tempo de aparecer.
    const capturar = (el: HTMLElement): void => {
      const nativeTitle = el.getAttribute('title');
      if (!nativeTitle) return;
      el.setAttribute('data-cp-tip', nativeTitle);
      if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', nativeTitle);
      el.removeAttribute('title');
    };

    const exibir = (el: HTMLElement): void => {
      timer = null;
      const text = el.getAttribute('data-cp-tip');
      if (!text || !el.isConnected) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const placement: 'top' | 'bottom' = spaceBelow < 60 ? 'top' : 'bottom';
      const cx = Math.min(Math.max(r.left + r.width / 2, 44), window.innerWidth - 44);
      setTip({
        text,
        x: cx,
        y: placement === 'bottom' ? r.bottom + 6 : r.top - 6,
        placement,
      });
    };

    const onOver = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      const el = target?.closest?.('[title],[data-cp-tip]') as HTMLElement | null;
      if (!el) return;
      if (el === alvo) return;
      if (silencedEl && (el.contains(silencedEl) || silencedEl.contains(el))) return;
      if (alvo) hide();
      capturar(el);
      alvo = el;
      timer = setTimeout(() => exibir(el), ATRASO_MS);
    };

    const onOut = (e: MouseEvent): void => {
      const related = e.relatedTarget as Node | null;
      if (silencedEl && (!related || !silencedEl.contains(related))) silencedEl = null;
      if (!alvo) return;
      if (!related || !alvo.contains(related)) hide();
    };

    const onClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      // O botão/link clicado (mesmo sem tooltip agora — ex.: o menu do topo
      // tira o próprio tooltip enquanto aberto e devolve ao fechar).
      silencedEl =
        (target?.closest?.('[title],[data-cp-tip],button,a') as HTMLElement | null) ?? target;
      hide();
    };

    document.addEventListener('mouseover', onOver, true);
    document.addEventListener('mouseout', onOut, true);
    window.addEventListener('scroll', hide, true);
    document.addEventListener('click', onClick, true);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('mouseover', onOver, true);
      document.removeEventListener('mouseout', onOut, true);
      window.removeEventListener('scroll', hide, true);
      document.removeEventListener('click', onClick, true);
    };
  }, []);

  // Entrada: fade + deslize vindo do lado do elemento (embaixo → desce;
  // em cima → sobe). Sem animação pra quem pediu menos movimento no sistema.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!tip || !el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const base = baseTransform(tip.placement);
    const desloc = tip.placement === 'bottom' ? -DESLOCAMENTO_PX : DESLOCAMENTO_PX;
    el.animate(
      [
        { opacity: 0, transform: `${base} translateY(${desloc}px)` },
        { opacity: 1, transform: base },
      ],
      { duration: DURACAO_MS, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
    );
  }, [tip]);

  if (!tip) return null;
  return (
    <div
      ref={tipRef}
      role="tooltip"
      style={{
        position: 'fixed',
        left: tip.x,
        top: tip.y,
        transform: baseTransform(tip.placement),
        zIndex: 9999,
        pointerEvents: 'none',
        background: '#0F172A',
        color: '#fff',
        fontSize: '11px',
        fontWeight: 500,
        lineHeight: 1.3,
        padding: '4px 8px',
        borderRadius: '6px',
        maxWidth: '280px',
        whiteSpace: 'nowrap',
      }}
    >
      {tip.text}
    </div>
  );
}
